import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project_type: 'general',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-fill message with subject from URL query (e.g., from Resources page)
  useEffect(() => {
    const subject = searchParams.get('subject');
    if (subject) {
      setFormData(prev => ({
        ...prev,
        message: `Resource Request: ${subject}\n\nI would like to request the following document/resource: ${subject}\n\nPlease share the details.\n\n`
      }));
    }
  }, [searchParams]);

  const projectTypes = [
    { value: 'marine', label: t('contact.projectTypes.marine') },
    { value: 'pipeline', label: t('contact.projectTypes.pipeline') },
    { value: 'offshore', label: t('contact.projectTypes.offshore') },
    { value: 'energy', label: t('contact.projectTypes.energy') },
    { value: 'general', label: t('contact.projectTypes.general') }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const submissionData = {
        ...formData,
        language: i18n.language
      };

      await axios.post(`${API}/contact`, submissionData);
      
      setStatus({
        type: 'success',
        message: t('contact.form.success')
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        project_type: 'general',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({
        type: 'error',
        message: t('contact.form.error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="contact-title">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-8 rounded-2xl shadow-lg"
              >
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      data-testid="contact-name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      data-testid="contact-email"
                    />
                  </div>

                  {/* Phone & Company */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.phone')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.company')}
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        data-testid="contact-company"
                      />
                    </div>
                  </div>

                  {/* Project Type */}
                  <div>
                    <label htmlFor="project_type" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.projectType')} *
                    </label>
                    <select
                      id="project_type"
                      name="project_type"
                      required
                      value={formData.project_type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      data-testid="contact-project-type"
                    >
                      {projectTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  {/* Status Message */}
                  {status.message && (
                    <div
                      className={`p-4 rounded-lg flex items-center space-x-2 ${
                        status.type === 'success'
                          ? 'bg-green-50 text-green-800 border border-green-200'
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}
                      data-testid="contact-status"
                    >
                      {status.type === 'success' ? (
                        <CheckCircle size={20} />
                      ) : (
                        <AlertCircle size={20} />
                      )}
                      <span>{status.message}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    <Send size={20} />
                    <span>{isSubmitting ? 'Sending...' : t('contact.form.submit')}</span>
                  </button>
                </form>
              </motion.div>
            </div>

            {/* Contact Information */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                {/* Office Locations Header */}
                <h3 className="text-2xl font-bold text-slate-900">
                  {t('contact.office.title')}
                </h3>

                {/* Istanbul Office */}
                <div className="bg-slate-50 p-6 rounded-2xl shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-900 p-3 rounded-lg flex-shrink-0">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2">
                        {t('contact.office.istanbul.title')}
                      </h4>
                      <p className="text-gray-600 text-sm whitespace-pre-line">
                        {t('contact.office.istanbul.address')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dakar Office */}
                <div className="bg-slate-50 p-6 rounded-2xl shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="bg-amber-600 p-3 rounded-lg flex-shrink-0">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2">
                        {t('contact.office.dakar.title')}
                      </h4>
                      <p className="text-gray-600 text-sm whitespace-pre-line">
                        {t('contact.office.dakar.address')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-slate-50 p-6 rounded-2xl shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="bg-amber-500 p-3 rounded-lg flex-shrink-0">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-2">Email</h4>
                      <a
                        href="mailto:info@selimarine.com"
                        className="text-blue-900 hover:text-blue-700 font-medium"
                      >
                        {t('contact.office.email')}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Service Areas */}
                <div className="bg-gradient-to-br from-blue-900 to-slate-900 text-white p-6 rounded-2xl shadow-lg">
                  <h3 className="font-bold text-xl mb-2">
                    {t('contact.regions.title')}
                  </h3>
                  <p className="text-blue-200 text-sm">
                    {t('contact.regions.subtitle')}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
